import type { JSX } from "react";
import { Link, useLocation } from "react-router";
import type { ServiceStatus } from "../../api/schemas";
import { routePaths } from "../../app/navigation";
import { useOffline } from "../../offline/OfflineContext";
import { usePurchasePaths } from "../../platform/PlatformContext";
import { hasAccessRight } from "../../shared/auth/accessRights";
import { useI18n } from "../../shared/i18n/I18nContext";
import type { MessageKey } from "../../shared/i18n/messages.de";
import { useMe } from "../../shared/session/useMe";
import { Alert } from "../../shared/ui/Alert";
import styles from "../../shared/ui/ui.module.css";
import { tenantSectionPath } from "../tenant/tenantSections";
import { formatInstantDay } from "./model/display";

/**
 * Banner messages for people who pay (`ManageBilling`) where ways to buy may be shown: they are
 * asked to check the payment method (UX-019).
 */
const billingMessages: Readonly<Record<ServiceStatus, MessageKey | null>> = {
  Active: null,
  PastDue: "serviceStatus.pastDueForBilling",
  Suspended: "serviceStatus.suspendedForBilling",
  ExportWindow: "serviceStatus.exportWindow",
};

/**
 * Banner messages for the other administrators where ways to buy may be shown: they learn who
 * changes the payment method instead of being asked to do it themselves (UX-019).
 */
const administrationMessages: Readonly<Record<ServiceStatus, MessageKey | null>> = {
  Active: null,
  PastDue: "serviceStatus.pastDueForAdministration",
  Suspended: "serviceStatus.suspendedForAdministration",
  ExportWindow: "serviceStatus.exportWindow",
};

/**
 * Banner messages of the native apps: only the consequence, without payment request (ADR-0087).
 * A due payment restricts nothing yet, so it shows no banner there.
 */
// LEGAL-REVIEW: whether the neutral note on the read-only administration counts as encouragement
// to buy outside the app (Apple 3.1.3, Google Play "Payments"; ADR-0087).
const nativeMessages: Readonly<Record<ServiceStatus, MessageKey | null>> = {
  Active: null,
  PastDue: null,
  Suspended: "serviceStatus.readOnly",
  ExportWindow: "serviceStatus.exportWindow",
};

/**
 * Returns whether the roles administer the tenant (master data or tenant settings).
 *
 * @param roles - Roles of the signed-in person.
 * @returns `true` for administrators.
 */
function administers(roles: readonly string[]): boolean {
  const administrator =
    hasAccessRight(roles, "ManageMasterData") || hasAccessRight(roles, "ManageTenant");

  return administrator;
}

/**
 * Returns the banner message for the service state of the tenant (spec 20.3.3, T-137, UX-019).
 * In the browser, administrators and the people responsible for billing see it: those who may pay
 * (`ManageBilling`) are asked to check the payment method, the others learn who does. In the native
 * apps only administrators see the consequence, because the apps offer no billing (ADR-0087).
 * Clocking and own times are never affected (20.1 B-3), so employees are not bothered with the
 * payment state of their employer.
 *
 * @param status - Service state of the tenant.
 * @param roles - Roles of the signed-in person.
 * @param purchasePaths - Whether ways to buy may be shown; `false` in the native apps.
 * @returns The message key, or `null` if no banner is shown.
 */
export function serviceStatusMessage(
  status: ServiceStatus,
  roles: readonly string[],
  purchasePaths: boolean,
): MessageKey | null {
  const paysForIt = hasAccessRight(roles, "ManageBilling");
  const concerned = administers(roles) || paysForIt || hasAccessRight(roles, "ReadSubscription");
  let message: MessageKey | null;

  if (!purchasePaths) {
    message = administers(roles) ? nativeMessages[status] : null;
  } else if (!concerned) {
    message = null;
  } else {
    message = (paysForIt ? billingMessages : administrationMessages)[status];
  }

  return message;
}

/**
 * Banner above every screen while a payment is due, the administration is read-only or the
 * contract has ended (T-137); in the export window with the date of deletion (T-138). Every state
 * shown is a warning (UX-019). People who may export get a link to the data export in the export
 * window; people who may see the subscription get a link to the billing page, except in the native
 * apps (ADR-0087). The link is a 44 px target (UX-104). On the billing page the plan section names
 * a due payment or the read-only administration in context, so the banner appears there only in
 * the export window, with the deletion date.
 *
 * @returns The banner, or `null`.
 */
export function ServiceStatusBanner(): JSX.Element | null {
  const { t, languageTag } = useI18n();
  const me = useMe();
  const offline = useOffline();
  const location = useLocation();
  const purchasePaths = usePurchasePaths();
  const roles = me.data?.roles ?? [];
  const onBillingPage = location.pathname === routePaths.billing;
  const message =
    me.data === undefined || (onBillingPage && me.data.serviceStatus !== "ExportWindow")
      ? null
      : serviceStatusMessage(me.data.serviceStatus, roles, purchasePaths);

  if (message === null) {
    return null;
  }

  // In the export window the deletion date is named and the export is the way forward (T-138).
  const endsAt =
    me.data?.serviceStatus === "ExportWindow" ? (me.data.exportWindowEndsAt ?? null) : null;
  const timeZone = me.data?.employment?.timeZoneId ?? offline.deviceTimeZone;
  const text =
    endsAt === null
      ? t(message)
      : t("serviceStatus.exportWindowUntil", {
          day: formatInstantDay(endsAt, languageTag, timeZone),
        });
  const toBilling = purchasePaths && hasAccessRight(roles, "ReadSubscription") && !onBillingPage;
  const banner = (
    <Alert tone="warning">
      <p>{text}</p>
      {endsAt !== null && hasAccessRight(roles, "ExportTenantData") ? (
        <Link className={styles["linkButton"]} to={tenantSectionPath("contract")}>
          {t("serviceStatus.toExport")}
        </Link>
      ) : toBilling ? (
        <Link className={styles["linkButton"]} to={routePaths.billing}>
          {t("serviceStatus.toBilling")}
        </Link>
      ) : null}
    </Alert>
  );

  return banner;
}
